import * as THREE from 'three';
import type { FeatureResultMesh } from './FeatureResultMesh';
import { cloneOwnedMaterial, disposeOwnedMaterial, LineMaterialFactory, LineMaterialState } from '../../material';
import type { FeatureLineMaterialEntry, FeatureLineMaterialState } from '../../../types';
/** 特征线默认命名计数器。 */
let featureLineNameCounter = 1;

/**
 * 判断对象是否为特征边线对象。
 *
 * @param value 待判断对象。
 * @returns 是否为特征边线对象。
 */
export function isFeatureLine(value: unknown): value is FeatureLine {
  if (!value || typeof value !== 'object') {
    return false;
  }
  return Reflect.get(value, 'isFeatureLine') === true;
}

/**
 * 特征边线对象。
 *
 * 设计说明：
 * 1. 直接继承 `THREE.Line`，保持与当前交互状态系统兼容；
 * 2. 通过显式类成员保存边索引、相邻面索引与元数据；
 * 3. 新的内部逻辑只读取显式成员；
 * 4. 世界坐标端点计算通过 getWorldEndpoints / getWorldDirection 封装，外部无需直接读取几何属性。
 */
export class FeatureLine extends THREE.Line {
  /** 运行时类型标记。 */
  public readonly isFeatureLine: boolean = true;

  /** 所属结果体对象 ID。 */
  public objectId: string | null = null;

  /** 边索引。 */
  public edgeIndex: number | null = null;

  /** 权威子拓扑 ID。 */
  public subEntityId: string | null = null;

  /** 持久化边引用。 */
  public persistentRef: string | null = null;

  /** 相邻面索引。 */
  public adjacentFaceIndices: number[] = [];

  /** 相邻面持久化引用。 */
  public adjacentFaceRefs: string[] = [];

  /** 特征元数据。 */
  private featureMetadata: Record<string, unknown> = {};

  /** 所属权威结果体。*/
  private owningResultMesh: FeatureResultMesh | null = null;

  /** 短命代理不能把 RenderObject 挂到全局模板上，各状态材质由该线独占。 */
  private readonly stateMaterials = new Map<FeatureLineMaterialState, FeatureLineMaterialEntry>();

  /** 防止宿主回收与外部清理重复派发资源销毁事件。 */
  private disposed = false;

  /**
   * 构造函数。
   *
   * @param geometry 线几何。
   */
  public constructor(geometry: THREE.BufferGeometry) {
    const template = LineMaterialFactory.getFeatureLineMaterialByState(LineMaterialState.Default);
    const material = cloneOwnedMaterial(template);
    material.depthWrite = false;
    super(geometry, material);
    this.stateMaterials.set('default', {template, templateVersion: template.version, material});
    this.name = `线${featureLineNameCounter}`;
    featureLineNameCounter += 1;
    this.renderOrder = 2;
    this.frustumCulled = false;
  }

  /**
   * 获取本线独占的状态材质；相同配方热态复用，配方变化只替换对应槽。
   * @param state 默认、移入或选中状态。
   * @param template 借用的交互样式模板，禁止作为本线实际渲染材质。
   * @returns 随本线销毁的材质，调用方不得在切换状态时单独释放。
   */
  public getInteractionMaterial(state: FeatureLineMaterialState, template: THREE.Material): THREE.Material {
    if (this.disposed) throw new Error(`无法获取已释放拓扑线的交互材质：${this.uuid}`);
    const cached = this.stateMaterials.get(state);
    if (cached?.template === template && cached.templateVersion === template.version) return cached.material;
    // 先生成新配方再替换；克隆失败时仍保留原状态和已存在的绑定。
    const material = cloneOwnedMaterial(template);
    material.depthWrite = false;
    this.stateMaterials.set(state, {template, templateVersion: template.version, material});
    cached?.material.dispose();
    return material;
  }

  /**
   * 结束拓扑线代理生命周期，释放其几何和所有曾参与绘制的独占状态材质。
   * 材质 dispose 驱动 RenderObject/绑定清理；共享模板由其原有所有者负责。
   */
  public dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.removeFromParent();
    this.geometry.dispose();
    const materials = new Set(Array.from(this.stateMaterials.values(), entry => entry.material));
    // 兼容命令临时接管后的当前材质，同时遵守外部共享材质所有权。
    (Array.isArray(this.material) ? this.material : [this.material]).forEach(material => materials.add(material));
    materials.forEach(material => disposeOwnedMaterial(material));
    this.stateMaterials.clear();
    this.owningResultMesh = null;
    this.featureMetadata = {};
  }

  /**
   * 设置特征边线上下文。
   *
   * @param input 上下文参数。
   */
  public setFeatureContext(input: {
    objectId: string;
    metadata: Record<string, unknown>;
    edgeIndex: number;
    subEntityId: string;
    persistentRef: string;
    adjacentFaceIndices: number[];
    adjacentFaceRefs: string[];
  }): void {
    this.objectId = input.objectId;
    this.edgeIndex = input.edgeIndex;
    this.subEntityId = input.subEntityId;
    this.persistentRef = input.persistentRef;
    this.adjacentFaceIndices = [...input.adjacentFaceIndices];
    this.adjacentFaceRefs = [...input.adjacentFaceRefs];
    this.featureMetadata = {
      ...input.metadata,
    };
  }

  /**
   * 读取特征元数据副本。
   *
   * @returns 元数据副本。
   */
  public getFeatureMetadata(): Record<string, unknown> {
    return { ...this.featureMetadata };
  }

  //------ 参考平面几何读取封装 开始------
  /**
   * 解析当前边代理应使用的世界矩阵。
   *
   * 设计说明：
   * 1. 常规情况下边代理挂在结果体下，直接使用自身 `matrixWorld` 即可；
   * 2. 面边界线重绑后，边代理可能只恢复到 `boundaryLines` 引用中，尚未重新挂入场景树；
   * 3. 这类未挂载边代理的几何仍然位于结果体局部坐标系，因此需要退回所属结果体的世界矩阵做变换。
   * 4. 曲线子类读取解析圆参数时也必须使用这条变换链，不能单独读取代理 matrixWorld。
   *
   * @returns 用于局部点转世界点的矩阵。
   */
  protected resolveWorldMatrixForEndpoints(): THREE.Matrix4 {
    const owningResultMesh = this.getOwningResultMesh();
    if (owningResultMesh && !this.isAttachedUnderObject(owningResultMesh)) {
      owningResultMesh.updateWorldMatrix(true, false);
      return owningResultMesh.matrixWorld;
    }

    if (this.parent) {
      this.updateWorldMatrix(true, false);
      return this.matrixWorld;
    }

    if (owningResultMesh) {
      owningResultMesh.updateWorldMatrix(true, false);
      return owningResultMesh.matrixWorld;
    }

    this.updateWorldMatrix(true, false);
    return this.matrixWorld;
  }

  /**
   * 判断当前边代理是否处在指定对象的子树内。
   *
   * 设计说明：
   * imported topology 的边代理可能只作为拓扑缓存对象存在，`parent` 非空并不等于它已经
   * 挂在可移动刚体的有效父链下。只有确认当前代理位于 owning result mesh 子树内时，
   * `matrixWorld` 才能代表当前装配体姿态；否则必须回退到 owning result mesh 的世界矩阵。
   *
   * @param ancestor 期望的父级对象。
   * @returns 是否位于该父级对象子树内。
   */
  private isAttachedUnderObject(ancestor: THREE.Object3D): boolean {
    let current: THREE.Object3D | null = this;
    while (current) {
      if (current === ancestor) {
        return true;
      }
      current = current.parent;
    }
    return false;
  }

  /**
   * 读取线段两端点（世界坐标）。
   *
   * 从几何体位置属性中取前两个顶点，应用 matrixWorld 后返回。
   * 顶点数不足 2 时返回 null。
   *
   * @returns 起终点世界坐标；几何数据不足时返回 null。
   */
  public getWorldEndpoints(): LineSegmentEndpoints | null {
    const start = new THREE.Vector3();
    const end = new THREE.Vector3();
    return this.copyWorldEndpointsTo(start, end) ? {start, end} : null;
  }

  /**
   * 将线段世界端点写入调用方提供的向量。
   * 高频求解链使用该入口复用向量，避免每次读取端点都创建对象和两支 Vector3。
   *
   * @param startOut 起点输出向量。
   * @param endOut 终点输出向量。
   * @returns 几何至少包含两个顶点时返回 true。
   */
  public copyWorldEndpointsTo(startOut: THREE.Vector3, endOut: THREE.Vector3): boolean {
    const posAttr = this.geometry.getAttribute('position');
    if (!(posAttr instanceof THREE.BufferAttribute) || posAttr.count < 2) {
      return false;
    }
    const worldMatrix = this.resolveWorldMatrixForEndpoints();
    startOut.set(posAttr.getX(0), posAttr.getY(0), posAttr.getZ(0)).applyMatrix4(worldMatrix);
    endOut.set(posAttr.getX(1), posAttr.getY(1), posAttr.getZ(1)).applyMatrix4(worldMatrix);
    return true;
  }

  /**
   * 读取当前边/曲线几何的世界空间采样点。
   *
   * 设计说明：
   * imported topology 的边代理可能不会重新挂入场景树，只通过 `owningResultMesh`
   * 保留与可见几何的归属关系。上层若直接读取 `matrixWorld` 会在这类场景下拿到
   * identity，导致圆边、圆柱面轴线被误解析为局部坐标。因此采样点必须复用端点读取
   * 已经收口好的世界矩阵规则。
   *
   * @returns 世界空间采样点；几何无有效 position 属性时返回空数组。
   */
  public getWorldSamplePoints(): THREE.Vector3[] {
    const posAttr = this.geometry.getAttribute('position');
    if (!(posAttr instanceof THREE.BufferAttribute)) {
      return [];
    }

    const worldMatrix = this.resolveWorldMatrixForEndpoints();
    const points: THREE.Vector3[] = [];
    for (let pointIndex = 0; pointIndex < posAttr.count; pointIndex += 1) {
      points.push(new THREE.Vector3(
        posAttr.getX(pointIndex),
        posAttr.getY(pointIndex),
        posAttr.getZ(pointIndex),
      ).applyMatrix4(worldMatrix));
    }
    return points;
  }

  /**
   * 读取线段方向向量（世界坐标，单位化）。
   *
   * 方向由起点指向终点，长度过短（< 1e-12）时视为退化，返回 null。
   *
   * @returns 单位化方向向量；无法计算时返回 null。
   */
  public getWorldDirection(): THREE.Vector3 | null {
    const endpoints = this.getWorldEndpoints();
    if (!endpoints) {
      return null;
    }
    const d = endpoints.end.clone().sub(endpoints.start);
    return d.length() < 1e-12 ? null : d.normalize();
  }

  /**
   * 读取线段中点（世界坐标）。
   *
   * 参考平面求解在将线作为点源使用时，以线段中点作为统一语义。
   *
   * @returns 世界中点；几何数据不足时返回 null。
   */
  public getWorldMidpoint(): THREE.Vector3 | null {
    const endpoints = this.getWorldEndpoints();
    if (!endpoints) {
      return null;
    }
    return endpoints.start.clone().add(endpoints.end).multiplyScalar(0.5);
  }
  //------ 参考平面几何读取封装 结束------

  /**
   * 设置当前边代理所属的权威结果体。
   *
   * @param mesh 所属结果体；传入 `null` 表示清空绑定。
   */
  public setOwningResultMesh(mesh: FeatureResultMesh | null): void {
    this.owningResultMesh = mesh;
  }

  /**
   * 读取当前边代理所属的权威结果体。
   *
   * @returns 所属结果体；未绑定时返回 `null`。
   */
  public getOwningResultMesh(): FeatureResultMesh | null {
    return this.owningResultMesh;
  }
}
